import React, { useEffect, useRef, useState } from 'react'
import { PersonDto, PersonLiteDto } from '../../api/person/models/PersonDto'
import personApi from '../../api/person/PersonApi'
import Card from '../Card'
import PersonUi from '../PersonUi'
import JobTimeline from '../JobTimeline'
import { useManageRoleStore } from './stateStore/manageRoleStore'
import LoadingSpinner from '../LoadingSpinner'
import Note from '../Note'
import { NoteType } from '../../enums/NoteType'
import ExpandableText from '../ExpandableText'
import { formatText, getCountryFlag } from '../../utils/personUtils'
import Connection from '../Connection'
import FeedbackSection from '../FeedbackSection'
import Section from '../Section'
import { useSafeUserData } from '../../stateStores/User'
import { AuthHelper } from '../../auth/AuthHelper'
import { UserRoles } from '../../api/tenant/models/UserDto'
import CandidateControls from './CandidateControls'
import CandidateStatus from './CandidateStatus'

export default function CandidateRightPane(): React.JSX.Element {
    const selectedCandidateId = useManageRoleStore(
        (state) => state.selectedCandidateId,
    )
    const [person, setPerson] = useState<PersonDto | undefined>(undefined)
    const [personLite, setPersonLite] = useState<PersonLiteDto | undefined>(
        undefined,
    )
    const prevSelectedCandidateIdRef = useRef<string | undefined>(undefined)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const candidates = useManageRoleStore((state) => state.candidates)
    const personLiteCache = useManageRoleStore().getState().personLiteCache
    const candidate = useManageRoleStore((state) =>
        state.candidates.find(
            (c) => c.candidatePersonId === selectedCandidateId,
        ),
    )
    const { user } = useSafeUserData()
    // const updatePresentForCandidate = useManageRoleStore(
    //     (state) => state.updatePresentForCandidate,
    // )

    useEffect(() => {
        const fetchPersonData = async () => {
            try {
                const candidate = candidates.find(
                    (c) => c.candidatePersonId === selectedCandidateId,
                )
                if (!candidate) {
                    throw new Error(
                        `Candidate not found in CandidateRightPane for id: ${selectedCandidateId}`,
                    )
                }
                const personLite = personLiteCache.getOrThrow(
                    candidate.candidatePersonId,
                )

                setPersonLite(personLite)

                // TODO : Handle error
                const response = await personApi.getPersonById(personLite.id)
                // the user can flick between candidates faster than we can fetch them
                //  so we need to make sure the person we fetched relates to the most recent person
                //  that has been selected - just ignore the response if it doesn't match
                if (response.person.id === prevSelectedCandidateIdRef.current) {
                    setPerson(response.person)
                }
            } catch (err) {
                console.error(err)
                setErrMsg(
                    'There was a problem loading the candidate. Please refresh and try again.',
                )
            }
        }

        if (
            prevSelectedCandidateIdRef.current &&
            selectedCandidateId === prevSelectedCandidateIdRef.current
        ) {
            // we are already showing this candidate - no need to re-fetch
            return
        }

        prevSelectedCandidateIdRef.current = selectedCandidateId

        setPerson(undefined)
        setPersonLite(undefined)
        setErrMsg(undefined)

        fetchPersonData()
    }, [selectedCandidateId, candidates, personLiteCache])

    if (errMsg) {
        return (
            <Note type={NoteType.ERROR} containerClassNames="bg-white">
                {errMsg}
            </Note>
        )
    }

    return (
        <div>
            {personLite && (
                <Card
                    classes="bg-white"
                    footer={
                        <div className="flex mt-[-0.5rem] flex-col">
                            <div>{person?.headline}</div>
                            <div className="text-gray-600 text-sm">
                                {getCountryFlag(
                                    person?.location
                                        ?.split(',')
                                        .pop()
                                        ?.trim() ?? '',
                                )}
                                <span className="ml-2">{person?.location}</span>
                            </div>
                        </div>
                    }
                >
                    <div className="flex mb-2 justify-between">
                        <PersonUi
                            person={personLite}
                            showCurrentJobDetails={false}
                            nameFontSize="text-[1.6rem]"
                            imageClasses="w-[130px] h-[130px]"
                        />
                        {!person && <LoadingSpinner show={true} />}
                        {person && candidate && (
                            <CandidateStatus candidate={candidate} />
                        )}
                    </div>
                </Card>
            )}

            {person && candidate && (
                <Section className="border rounded-lg px-4 pt-4 bg-white border-gray-300 mt-2">
                    <CandidateControls candidate={candidate} />
                </Section>
            )}

            {person && !AuthHelper.userHasRole(user, UserRoles.TA) && (
                <div className="border rounded-lg px-4 pt-4 bg-white border-gray-300 mt-2">
                    <FeedbackSection />
                </div>
            )}
            {person && candidate && (
                <Section
                    title="Connections"
                    titleType="h2"
                    collapsible={true}
                    defaultCollapsed={true}
                    className="border rounded-lg p-4 bg-white border-gray-300 mt-2"
                >
                    <div className="text-sm">
                        {candidate.connections.map((connection, index) => (
                            <div key={connection.connectedPersonId}>
                                <Connection connection={connection} />
                                {index !== candidate.connections.length - 1 && (
                                    <div className="w-full border-b border-gray-200 my-4"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
            )}
            {person?.summary && (
                <Section
                    title="About"
                    titleType="h2"
                    className="border rounded-lg p-4 bg-white border-gray-300 mt-2"
                >
                    <ExpandableText
                        className="text-sm whitespace-pre-line text-gray-600"
                        text={formatText(person.summary)}
                        maxLines={4}
                    />
                </Section>
            )}
            {person?.workedAts && person.workedAts.length > 0 && (
                <div className="border rounded-lg bg-white border-gray-300 mt-2">
                    <div className="mt-4">
                        <JobTimeline jobs={person.workedAts} />
                    </div>
                </div>
            )}

            {/* <div className="mt-4">
                TODO:
                <ul className="list-disc ml-4">
                    <li>
                        Reasons included in search results NOTE: we can't do
                        this yet (actually we can show the current scores from
                        the closeness/job relevance) but this could include any
                        links to companies in the network seed. Then the company
                        connections wouldn't need to be in the CONTXT
                        connections section and that can focus more on person to
                        person connections.
                    </li>
                    <li>
                        Experience - NOTE: companies should be clickable. This
                        should show the company in this right pane.
                    </li>
                </ul>
            </div>
            <div className="mt-4">
                in the future we need to include things such as:
                <ul className="list-disc ml-4">
                    <li>Present (editable to TA, hidden from HM)</li>
                    <li>
                        Would work with again + comments (editable by HM, hidden
                        from everyone except the specific HM)
                    </li>
                    <li>Suitable? (used to be called "Interview?")</li>
                </ul>
            </div> */}
        </div>
    )
}
